import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { PoTheme, PoThemeA11yEnum, PoThemeService, PoThemeTypeEnum } from '@po-ui/ng-components';

const ASCENSUS_THEME: PoTheme = {
  name: 'ascensus',
  type: {
    light: {
      color: {
        brand: {
          '01': {
            lightest: '#e8f4fa',
            lighter:  '#b8dcea',
            light:    '#4ba4c7',
            base:     '#1787b4',
            dark:     '#136e94',
            darker:   '#0f5f80',
            darkest:  '#0a445e'
          },
          '02': {
            base: '#2d3e50'
          },
          '03': {
            base: '#f39c12'
          }
        },
        action: {
          default:  'var(--color-brand-01-base)',
          hover:    'var(--color-brand-01-dark)',
          pressed:  'var(--color-brand-01-darker)',
          disabled: 'var(--color-neutral-light-30)',
          focus:    'var(--color-brand-01-darkest)'
        },
        neutral: {
          light: {
            '00': '#ffffff',
            '05': '#fbfbfb',
            '10': '#eceeee',
            '20': '#dadedf',
            '30': '#b6bdbf'
          },
          mid: {
            '40': '#9da7a9',
            '60': '#6e7c7f'
          },
          dark: {
            '70': '#4a5c60',
            '80': '#2c3739',
            '90': '#1d2426',
            '95': '#0b0e0e'
          }
        },
        feedback: {
          negative: {
            lightest: '#f6e6e5', lighter: '#e3aeab', light: '#d58581',
            base:     '#be3e37',
            dark:     '#9b2d27', darker:  '#72211d', darkest: '#4a1512'
          },
          info: {
            lightest: '#e3e9f7', lighter: '#b0c1e8', light: '#7996d7',
            base:     '#23489f',
            dark:     '#173782', darker:  '#0f2557', darkest: '#081536'
          },
          positive: {
            lightest: '#def7ed', lighter: '#7ecead', light: '#41b483',
            base:     '#107048',
            dark:     '#0f5236', darker:  '#083a25', darkest: '#002415'
          },
          warning: {
            lightest: '#fcf6e3', lighter: '#f7dd97', light: '#f1cd6a',
            base:     '#efba2a',
            dark:     '#d8a20e', darker:  '#705200', darkest: '#473400'
          }
        }
      }
    },
    dark: {
      color: {
        brand: {
          '01': {
            lightest: '#0a445e',
            lighter:  '#0f5f80',
            light:    '#136e94',
            base:     '#1787b4',
            dark:     '#4ba4c7',
            darker:   '#b8dcea',
            darkest:  '#e8f4fa'
          },
          '02': {
            base: '#3c5268'
          },
          '03': {
            base: '#f39c12'
          }
        },
        action: {
          default:  'var(--color-brand-01-dark)',
          hover:    'var(--color-brand-01-darker)',
          pressed:  'var(--color-brand-01-darkest)',
          disabled: 'var(--color-neutral-mid-40)',
          focus:    'var(--color-brand-01-darkest)'
        },
        neutral: {
          light: {
            '00': '#0b0e0e',
            '05': '#1d2426',
            '10': '#2c3739',
            '20': '#4a5c60',
            '30': '#6e7c7f'
          },
          mid: {
            '40': '#9da7a9',
            '60': '#b6bdbf'
          },
          dark: {
            '70': '#dadedf',
            '80': '#eceeee',
            '90': '#fbfbfb',
            '95': '#ffffff'
          }
        },
        feedback: {
          negative: {
            lightest: '#4a1512', lighter: '#72211d', light: '#9b2d27',
            base:     '#be3e37',
            dark:     '#d58581', darker:  '#e3aeab', darkest: '#f6e6e5'
          },
          info: {
            lightest: '#081536', lighter: '#0f2557', light: '#173782',
            base:     '#0079b8',
            dark:     '#7996d7', darker:  '#b0c1e8', darkest: '#e3e9f7'
          },
          positive: {
            lightest: '#002415', lighter: '#083a25', light: '#0f5236',
            base:     '#107048',
            dark:     '#41b483', darker:  '#7ecead', darkest: '#def7ed'
          },
          warning: {
            lightest: '#473400', lighter: '#705200', light: '#d8a20e',
            base:     '#efba2a',
            dark:     '#f1cd6a', darker:  '#f7dd97', darkest: '#fcf6e3'
          }
        }
      }
    },
    a11y: PoThemeA11yEnum.AAA
  },
  active: { type: PoThemeTypeEnum.light, a11y: PoThemeA11yEnum.AAA }
};

const STORAGE_KEY = 'asc-theme-type';

@Injectable({ providedIn: 'root' })
export class ThemeService {

  private readonly darkSubject = new BehaviorSubject<boolean>(false);

  /** Emite true quando o tema escuro esta ativo (emite valor atual no subscribe). */
  readonly isDark$: Observable<boolean> = this.darkSubject.asObservable();

  /**
   * Observable da URL da logo Ascensus apropriada para o tema atual.
   * Light -> logo positiva (versao colorida); Dark -> logo negativa (versao branca).
   */
  readonly logo$: Observable<string> = this.isDark$.pipe(
    map(dark => dark
      ? 'assets/brand/logo-ascensus-negativa.png'
      : 'assets/brand/logo-ascensus-positiva.png')
  );

  readonly secondaryLogo$: Observable<string> = this.isDark$.pipe(
    map(dark => dark
      ? 'assets/brand/logo-ascensus-positiva.png'
      : 'assets/brand/logo-ascensus-negativa.png')
  );

  /** Logo "invertida" — util quando o componente tem sempre fundo escuro (menu lateral). */
  readonly logoInverted$: Observable<string> = this.isDark$.pipe(
    map(() => 'assets/brand/logo-ascensus-negativa.png')
  );

  readonly secondaryLogoInverted$: Observable<string> = this.isDark$.pipe(
    map(() => 'assets/brand/logo-ascensus-positiva.png')
  );

  constructor(private poTheme: PoThemeService, private http: HttpClient) {}

  /** Aplica o tema Ascensus usando a preferencia salva (ou light como padrao). */
  applyInitial(): void {
    const saved = this.readSavedType();
    this.poTheme.setTheme(ASCENSUS_THEME, saved);
    this.darkSubject.next(saved === PoThemeTypeEnum.dark);
  }

  /**
   * Aplica a preferencia de dark mode vinda do JWT (login).
   * Atualiza localStorage, PoTheme e o BehaviorSubject.
   */
  applyFromLogin(dark: boolean): void {
    const type = dark ? PoThemeTypeEnum.dark : PoThemeTypeEnum.light;
    localStorage.setItem(STORAGE_KEY, String(type));
    this.poTheme.setCurrentThemeType(type);
    this.darkSubject.next(dark);
  }

  /** Alterna entre light e dark, persiste local e remoto (ZOA_DARK). */
  toggle(): PoThemeTypeEnum {
    const nextDark = !this.isDark();
    const next = nextDark ? PoThemeTypeEnum.dark : PoThemeTypeEnum.light;
    this.poTheme.setCurrentThemeType(next);
    localStorage.setItem(STORAGE_KEY, String(next));
    this.darkSubject.next(nextDark);

    // Persiste no backend (ZOA_DARK). Em caso de erro de rede, mantem o local.
    this.http.post('portalnf/auth/dark-mode', { dark: nextDark })
      .pipe(catchError(() => of(null)))
      .subscribe();

    return next;
  }

  isDark(): boolean {
    return this.darkSubject.value;
  }

  currentType(): PoThemeTypeEnum {
    return this.isDark() ? PoThemeTypeEnum.dark : PoThemeTypeEnum.light;
  }

  private readSavedType(): PoThemeTypeEnum {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === String(PoThemeTypeEnum.dark) ? PoThemeTypeEnum.dark : PoThemeTypeEnum.light;
  }
}
